import { Link } from 'react-router-dom';
import { AlertTriangle, Utensils, Droplets } from 'lucide-react';
import { useFeedingStatus } from '../../../hooks/useFeedingStatus';
import { useHydrationStatus } from '../../../hooks/useHydrationStatus';
import { usePetPhoto } from '../../../hooks/usePetPhoto';
import { useFeedingGroups } from '../../../contexts/FeedingGroupsContext';
import { useHydrationGroups } from '../../../contexts/HydrationGroupsContext';
import { usePets } from '../../../contexts/PetsContext';
import Card from '../../../components/ui/Card';
import Avatar from '../../../components/ui/Avatar';
import StatusTrafficLight from './StatusTrafficLight';
import FeedingButton from './FeedingButton';
import WaterButton from './WaterButton';
import { formatGrams, formatTimeAgo } from '../../../lib/format';
import { sumDispenserStock } from '../../../services/feedingGroupService';
import { estimateGroupProjection } from '../../../lib/learning';
import { INVENTORY } from '../../../config/constants';

/**
 * Tarjeta de mascota en la lista principal.
 * Layout mobile-first: foto/avatar a la izquierda, info al centro,
 * botón de alimentar a la derecha. En pantallas chicas, el botón
 * pasa abajo (flex-wrap).
 *
 * El click en la tarjeta lleva al detalle; el click en el botón
 * NO propaga (evita navegar al feed).
 */
const PetCard = ({ pet }) => {
  const feedingStatus = useFeedingStatus(pet);
  const { getGroupById } = useFeedingGroups();
  const { getGroupById: getHydrationGroupById } = useHydrationGroups();
  const { pets: allPets } = usePets();
  const { url: photoUrl } = usePetPhoto(pet);

  const group = getGroupById(pet.feedingGroupId);
  // Hidratación opt-in: solo se resuelve si la pet la activó. Si el grupo
  // referenciado no está en el context (ej. soft-deleted), hydrationGroup
  // queda null y la card vuelve al layout single (sin segundo semáforo ni
  // botón).
  const hydrationGroup = pet.hydrationGroupId
    ? getHydrationGroupById(pet.hydrationGroupId)
    : null;
  const hydrationStatus = useHydrationStatus(hydrationGroup);
  const lastFedMs = pet.lastFed?.toMillis?.() ?? null;
  const lastFedLabel = lastFedMs ? formatTimeAgo(lastFedMs) : 'Sin alimentar aún';

  // Proyección a nivel grupo (Fase 6): considera el stock total
  // (dispensers + bolsa principal + bolsas en reserva) y la suma del consumo
  // diario de TODAS las mascotas del grupo. El chip se muestra cuando hay
  // pets activas con consumo definido (sino daysRemaining es null).
  const groupPets = group
    ? allPets.filter((p) => p.feedingGroupId === group.id)
    : [];
  const projection = group
    ? estimateGroupProjection(group, groupPets, {
        defaultThresholdDays: INVENTORY.DEFAULT_LOW_STOCK_THRESHOLD_DAYS,
      })
    : null;
  const showDaysChip = projection && projection.daysRemaining != null;

  return (
    <Card className="p-0 overflow-hidden">
      <Link
        to={`/pets/${pet.id}`}
        className="block hover:bg-slate-50/50 transition-colors"
      >
        <div className="flex items-center gap-3 p-4">
          <Avatar src={photoUrl} name={pet.name} size="lg" />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-slate-900 truncate">{pet.name}</h3>
              <StatusTrafficLight
                status={feedingStatus.status}
                variant="badge"
                icon={Utensils}
              />
              {hydrationGroup && (
                <StatusTrafficLight
                  status={hydrationStatus.status}
                  variant="badge"
                  icon={Droplets}
                />
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {lastFedLabel}
            </p>
            {group ? (
              <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-600">
                {(group.dispensers ?? []).length > 0 ? (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-slate-400">
                      {group.dispensers.length === 1 ? 'Dispenser:' : `Dispensers (${group.dispensers.length}):`}
                    </span>
                    <strong>{formatGrams(sumDispenserStock(group.dispensers))}</strong>
                  </span>
                ) : (
                  <span className="text-slate-400">Sin dispenser</span>
                )}
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1">
                  <span className="text-slate-400">Bolsa:</span>
                  <strong>{formatGrams(group.mainStockGr)}</strong>
                </span>
              </div>
            ) : (
              <p className="text-xs text-slate-400 mt-1.5">Sin grupo asignado</p>
            )}

            {/* Fase 6: chip de proyección. La granularidad de "días" es por
                grupo, no por mascota — todas las pets del grupo comparten el
                mismo daysRemaining. */}
            {showDaysChip && (
              <div className="mt-1.5">
                {projection.isLowStock ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-status-red ring-1 ring-red-100">
                    <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                    Comprar pronto · ~{Math.floor(projection.daysRemaining)} día
                    {Math.floor(projection.daysRemaining) === 1 ? '' : 's'}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-100">
                    ~{Math.floor(projection.daysRemaining)} día
                    {Math.floor(projection.daysRemaining) === 1 ? '' : 's'} de stock
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </Link>

      {/* Botones fuera del Link para que el click no navegue al detalle.
          Cuando la pet tiene hidratación activa mostramos los dos botones
          50/50 (cada uno con flex-1 + fullWidth). Sin hidratación, solo
          el de alimentar ocupando todo el ancho — layout simétrico. */}
      <div className="border-t border-slate-100 p-3 bg-slate-50/50 flex gap-2">
        <div className="flex-1">
          <FeedingButton pet={pet} variant="compact" fullWidth />
        </div>
        {hydrationGroup && (
          <div className="flex-1">
            <WaterButton group={hydrationGroup} variant="compact" fullWidth />
          </div>
        )}
      </div>
    </Card>
  );
};

export default PetCard;